
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Building2, Loader2, User } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import type { IncidentFormData } from './types';
import { EntityLookup, type EntityOption } from '@/components/ui/entity-lookup';
import { searchPersons } from '@/lib/personsApi';
import { searchCompanies } from '@/lib/companiesApi';
import { getActiveIndustries } from '@/lib/industriesApi';
import type { Industry } from '@/types/industries';
import { useState, useEffect } from 'react';

interface CustomerSectionProps {
  form: UseFormReturn<IncidentFormData>;
  isViewMode?: boolean;
}

export function CustomerSection({ form, isViewMode = false }: CustomerSectionProps) {
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [loadingIndustries, setLoadingIndustries] = useState(true);

  // Load active industries
  useEffect(() => {
    async function fetchIndustries() {
      try {
        setLoadingIndustries(true);
        const data = await getActiveIndustries();
        setIndustries(data);
      } catch (error) {
        console.error('Error loading industries:', error);
      } finally {
        setLoadingIndustries(false);
      }
    }
    fetchIndustries();
  }, []);

  // Search function for customer lookup (polymorphic: person or company)
  const customerType = form.watch('customerType');
  
  const searchCustomers = async (query: string): Promise<EntityOption[]> => {
    if (customerType === 'person') {
      const results = await searchPersons(query, 20);
      return results.map(person => ({
        value: person.person_id,
        label: person.full_name,
        subtitle: person.email || person.phone_primary || undefined,
        metadata: { 
          type: 'person',
          documentType: person.identification_type,
          nationality: person.nationality 
        }
      }));
    } else if (customerType === 'company') {
      const results = await searchCompanies(query, 20);
      return results.map(company => ({
        value: company.id,
        label: company.legalName,
        subtitle: company.tradeName || company.taxIdHash || undefined,
        metadata: { 
          type: 'company',
          industry: company.industry
        }
      }));
    }
    return [];
  };

  return (
    <div className="space-y-6">
      <Alert>
        <User className="h-4 w-4" />
        <AlertDescription>
          Información del cliente afectado y su contexto industrial
        </AlertDescription>
      </Alert>

      {/* Customer Type & Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="customerType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tipo de Cliente *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value ?? ''} disabled={isViewMode}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="z-[var(--z-popover-in-dragging-dialog)]">
                  <SelectItem value="person">Persona</SelectItem>
                  <SelectItem value="company">Empresa</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="customerId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Cliente *</FormLabel>
              <FormControl>
                <EntityLookup
                  value={field.value}
                  onChange={field.onChange}
                  onSearch={searchCustomers}
                  placeholder={
                    !customerType 
                      ? "Primero seleccione tipo..." 
                      : customerType === 'person' 
                        ? "Buscar persona..." 
                        : "Buscar empresa..."
                  }
                  searchPlaceholder={
                    customerType === 'person' 
                      ? "Nombre, email o documento..." 
                      : "Nombre legal, comercial o RUT..."
                  }
                  emptyMessage={
                    !customerType 
                      ? "Seleccione tipo primero" 
                      : "No se encontraron resultados"
                  }
                  disabled={!customerType || isViewMode}
                  allowClear
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <Separator />

      {/* Industry */}
      <FormField
        control={form.control}
        name="industry"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Industria</FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value ?? ''}
              disabled={loadingIndustries || isViewMode}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={loadingIndustries ? "Cargando..." : "Seleccionar industria"} />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="z-[var(--z-popover-in-dragging-dialog)]">
                {loadingIndustries ? (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    <span className="text-xs">Cargando...</span>
                  </div>
                ) : industries.length === 0 ? (
                  <div className="py-2 text-center text-xs text-muted-foreground">
                    No hay industrias
                  </div>
                ) : (
                  industries.map((industry) => (
                    <SelectItem key={industry.id} value={industry.code}>
                      {industry.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <FormDescription>
              Contexto industrial del cliente
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
